package com.trevorwiebe.timesheet.punch.presentation

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.punch.presentation.uiUtils.UiPunch
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.Util.getTimeSheetStatus
import com.trevorwiebe.timesheet.core.domain.model.PunchType
import com.trevorwiebe.timesheet.core.presentation.ShiftBottomBar
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.AddHoursDialog
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.NativeTimePicker
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.botPadding
import com.trevorwiebe.timesheet.punch.presentation.composables.PunchItem
import kotlinx.coroutines.delay
import kotlin.time.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import timesheet.composeapp.generated.resources.Res
import timesheet.composeapp.generated.resources.baseline_info_outline_24
import timesheet.composeapp.generated.resources.play_circle
import timesheet.composeapp.generated.resources.stop_circle
import kotlin.time.ExperimentalTime

@OptIn(ExperimentalTime::class)
@Composable
fun PunchScreen(
    startDate: String? = null,
    endDate: String? = null,
    timeSheetId: String? = null,
    userId: String? = null,
    isAdmin: Boolean = false,
    onBack: () -> Unit = {},
    viewModel: PunchViewModel = koinViewModel { parametersOf(startDate, endDate, timeSheetId, userId, isAdmin) },
) {

    val staticState by viewModel.staticPunchState.collectAsState()
    val dynamicState by viewModel.dynamicPunchState.collectAsState()
    val elementVisibilityState by viewModel.elementVisibilityState.collectAsState()

    val bottomPadding = if(startDate == null)
        botPadding()
    else
        0.dp

    val listVisible = remember { mutableStateOf(false) }
    val shiftBottomBarVisible = remember { mutableStateOf(true) }
    val listState = rememberLazyListState()

    val currentDate = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
    val currentPayPeriodStartAndEnd = staticState.currentPeriod
    val isCurrentPeriod = currentPayPeriodStartAndEnd != null &&
            currentDate >= currentPayPeriodStartAndEnd.first.date &&
            currentDate <= currentPayPeriodStartAndEnd.second.date

    val contentUnavailable = staticState.timeSheetDateList.isEmpty()
    LaunchedEffect(contentUnavailable) {
        if (!contentUnavailable) {
            delay(100)
            listVisible.value = true
        }
    }

    val title = if (startDate == null) {
        "Current Pay Period"
    } else {
        val startDate = Util.toFriendlyDate(currentPayPeriodStartAndEnd?.first)
        val endDate = Util.toFriendlyDate(currentPayPeriodStartAndEnd?.second)
        "$startDate - $endDate"
    }

    Scaffold(
        topBar = {
            if (startDate == null) {
                TopBar(title)
            } else {
                TopBar(
                    title = title,
                    backIcon = { BackIcon() },
                    onBack = {
                        onBack()
                        shiftBottomBarVisible.value = false
                    }
                )
            }
        },
        bottomBar = {
            // Admins reuse the same confirm/submit bar to confirm a pay period on the employee's
            // behalf (e.g. when the employee was off and never submitted their hours).
            if (timeSheetId != null && shiftBottomBarVisible.value) {
                ShiftBottomBar(
                    onConfirmPayPeriod = {
                        viewModel.onEvent(PunchEvents.OnSetSubmitPayPeriodDialog(true))
                    },
                    status = getTimeSheetStatus(
                        dynamicState.timeSheet,
                        isCurrentPeriod
                    )
                )
            }
        }
    ) { paddingValues ->
        ResponsiveContent {

        if (contentUnavailable) {
            Box(
                modifier = Modifier
                    .padding(paddingValues)
                    .fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
            }
        } else {
            AnimatedVisibility(
                visible = listVisible.value,
                enter = fadeIn(animationSpec = tween(500)),
                exit = fadeOut()
            ) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.BottomCenter
                ) {

                    LazyColumn(
                        reverseLayout = true,
                        modifier = Modifier
                            .padding(paddingValues)
                            .fillMaxSize(),
                        state = listState
                    ) {
                        item {
                            Spacer(Modifier.height(bottomPadding))
                        }
                        item {
                            AddPunch(
                                loadingPunch = elementVisibilityState.punchLoading,
                                onPunch = {
                                    val punchType = if (dynamicState.isClockedIn())
                                        PunchType.OUT else PunchType.IN
                                    viewModel.onEvent(PunchEvents.OnPunch(punchType))
                                },
                                clockedIn = dynamicState.isClockedIn(),
                                showPunchButton = isCurrentPeriod && !staticState.isAdmin,
                                clockMessage = dynamicState.clockMessage,
                                onShowInfo = {
                                    viewModel.onEvent(PunchEvents.OnShowInfo(true))
                                }
                            )
                        }
                        items(staticState.timeSheetDateList) { todayDate ->
                            val holiday =
                                staticState.holidays.firstOrNull {
                                    holiday -> holiday.date == todayDate
                                }
                            PunchItem(
                                editable = staticState.isAdmin || dynamicState.timeSheet == null || dynamicState.timeSheet?.submitted == false,
                                date = todayDate,
                                punches = dynamicState.punches[todayDate]?.first ?: emptyList(),
                                holidayAndPtoPunches = dynamicState.punches[todayDate]?.second ?: emptyList(),
                                onShowConfirmDelete = { uiPunch ->
                                    viewModel.onEvent(
                                        PunchEvents.OnShowConfirmDeletePunchesSheet(
                                            uiPunch
                                        )
                                    )
                                },
                                onShowAddHours = {
                                    viewModel.onEvent(
                                        PunchEvents.OnShowAddHoursDialog(
                                            todayDate
                                        )
                                    )
                                },
                                onTimeSelected = { punch ->
                                    viewModel.onEvent(PunchEvents.OnSetTimePickerVisibility(punch))
                                },
                                onUpdateRate = {
                                    viewModel.onEvent(
                                        PunchEvents.OnUpdateRate(
                                            it.punchIn,
                                            it.punchOut
                                        )
                                    )
                                },
                                rateList = staticState.rateList,
                                hoursWorked = viewModel.getHoursWorked(
                                    todayDate,
                                    dynamicState.punches[todayDate],
                                ),
                                onAddPTO = {
                                    viewModel.onEvent(PunchEvents.OnAddPTO(todayDate))
                                },
                                holiday = holiday?.name
                            )
                        }
                    }
                }
            }
        }
        }
    }

    NativeDialog(
        visible = elementVisibilityState.showConfirmDeletePunchesSheetUiPunch != null,
        onConfirm = {
            viewModel.onEvent(PunchEvents.OnDeletePunches)
        },
        confirmText = "Delete",
        onDismiss = {
            viewModel.onEvent(PunchEvents.OnShowConfirmDeletePunchesSheet(null))
        },
        dismissText = "Cancel",
        title = "Are you sure?",
        message = "Are you sure you want to delete this time?"
    )

    AddHoursDialog(
        currentDate = elementVisibilityState.showAddHourDialogTime,
        onConfirm = { punchIn, punchOut ->
            viewModel.onEvent(PunchEvents.OnAddHours(punchIn, punchOut))
        },
        onDismiss = {
            viewModel.onEvent(PunchEvents.OnShowAddHoursDialog(null))
        },
        rateList = staticState.rateList.filter { it.userFacing },
        currentPunches = dynamicState.punches[elementVisibilityState.showAddHourDialogTime]?.first
    )

    NativeDialog(
        visible = elementVisibilityState.submitPayPeriodDialog,
        onConfirm = { viewModel.onEvent(PunchEvents.OnConfirmPayPeriod) },
        confirmText = "Confirm",
        onDismiss = { viewModel.onEvent(PunchEvents.OnSetSubmitPayPeriodDialog(false)) },
        dismissText = "Cancel",
        title = "Submit Pay Period",
        message = "Are you sure you want to submit this pay period?"
    )

    ReviewRequiredDialog(
        punches = elementVisibilityState.reviewRequiredPunches,
        onDismiss = { viewModel.onEvent(PunchEvents.OnDismissReviewRequiredDialog) },
    )

    NativeDialog(
        visible = elementVisibilityState.showPtoErrorDialog,
        onConfirm = {
            viewModel.onEvent(PunchEvents.OnShowPTODialog(false))
        },
        confirmText = "OK",
        title = "PTO Error",
        message = elementVisibilityState.ptoError,
        onDismiss = {},
        dismissText = null
    )

    val friendlyStartDate =
        remember(staticState) { Util.toFriendlyDate(staticState.currentPeriod?.first) }
    val friendlyEndDate =
        remember(staticState) { Util.toFriendlyDate(staticState.currentPeriod?.second) }

    val plainText = remember(staticState) {
        buildString {
            append("Current Period:\n")
            append("$friendlyStartDate - $friendlyEndDate\n\n")
            append("Total Hours Worked:\n")
            staticState.hoursMap.forEach { (rate, hours) ->
                append("$rate: $hours\n")
            }
        }
    }

    NativeDialog(
        visible = elementVisibilityState.showPayPeriodInfoSheet,
        onDismiss = { viewModel.onEvent(PunchEvents.OnShowInfo(false)) },
        onConfirm = { viewModel.onEvent(PunchEvents.OnShowInfo(false)) },
        confirmText = "Close",
        title = "Pay Period Info",
        message = plainText
    )

    NativeTimePicker(
        punch = dynamicState.punchToEdit,
        currentPunches = dynamicState.punches.values.flatMap { it.first },
        onDismiss = {
            viewModel.onEvent(PunchEvents.OnSetTimePickerVisibility(null))
        },
    ) { punch ->
        viewModel.onEvent(PunchEvents.OnUpdatePunch(punch))
    }

}

@Composable
private fun ReviewRequiredDialog(
    punches: List<UiPunch>,
    onDismiss: () -> Unit,
) {
    if (punches.isEmpty()) return

    Dialog(onDismissRequest = onDismiss) {
        Card(shape = RoundedCornerShape(16.dp)) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
            ) {
                Text(
                    text = "Issues Found",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = "Please resolve the following issues before submitting.",
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(12.dp))
                Column(
                    modifier = Modifier
                        .weight(1f, fill = false)
                        .verticalScroll(rememberScrollState())
                ) {
                    punches.forEachIndexed { index, uiPunch ->
                        if (index > 0) {
                            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
                        }
                        val date = Util.toFriendlyDate(uiPunch.punchIn.dateTime)
                        val inTime = Util.toFriendlyTime(uiPunch.punchIn.dateTime)
                        val outTime = uiPunch.punchOut?.let { Util.toFriendlyTime(it.dateTime) } ?: "—"
                        Text(
                            text = date,
                            fontWeight = FontWeight.SemiBold,
                            modifier = Modifier.fillMaxWidth(),
                        )
                        val punchInError = uiPunch.punchInError
                        if (punchInError != null) {
                            Text(
                                text = "In: $inTime",
                                fontSize = 16.sp,
                                color = MaterialTheme.colorScheme.secondary,
                                modifier = Modifier.fillMaxWidth(),
                            )
                            Text(
                                text = punchInError,
                                fontSize = 14.sp,
                                color = MaterialTheme.colorScheme.error,
                                modifier = Modifier.fillMaxWidth(),
                            )
                        }
                        val punchOutError = uiPunch.punchOutError
                        if (punchOutError != null) {
                            Text(
                                text = "Out: $outTime",
                                fontSize = 16.sp,
                                color = MaterialTheme.colorScheme.secondary,
                                modifier = Modifier.fillMaxWidth(),
                            )
                            Text(
                                text = punchOutError,
                                fontSize = 14.sp,
                                color = MaterialTheme.colorScheme.error,
                                modifier = Modifier.fillMaxWidth(),
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(16.dp))
                DialogButtonRow(
                    confirmButtonText = "OK",
                    onConfirmClick = onDismiss,
                )
            }
        }
    }
}

@Composable
private fun AddPunch(
    showPunchButton: Boolean,
    clockMessage: String?,
    loadingPunch: Boolean,
    clockedIn: Boolean,
    onShowInfo: () -> Unit,
    onPunch: () -> Unit,
) {
    if (showPunchButton) {
        Column (
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ){
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                IconButton(
                    onClick = onShowInfo
                ) {
                    Icon(
                        modifier = Modifier.padding(8.dp),
                        painter = painterResource(Res.drawable.baseline_info_outline_24),
                        contentDescription = "",
                        tint = MaterialTheme.colorScheme.tertiary
                    )
                }
                Spacer(modifier = Modifier.weight(1f))
                val buttonText = if (clockedIn) "Clock Out" else "Clock In"
                if(clockedIn) {
                    Button(
                        modifier = Modifier.width(150.dp).height(50.dp),
                        shape = CircleShape,
                        onClick = onPunch,
                        enabled = clockMessage.isNullOrEmpty(),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = MaterialTheme.colorScheme.error,
                            contentColor = MaterialTheme.colorScheme.onError,
                            disabledContainerColor = MaterialTheme.colorScheme.error.copy(alpha = 0.5f),
                            disabledContentColor = MaterialTheme.colorScheme.onError.copy(alpha = 0.5f),
                        ),
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            if (loadingPunch) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(24.dp),
                                    color = MaterialTheme.colorScheme.onError
                                )
                            } else {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(
                                        modifier = Modifier.size(20.dp),
                                        painter = painterResource(Res.drawable.stop_circle),
                                        contentDescription = null,
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(text = buttonText)
                                }
                            }
                        }
                    }
                }else{
                    Button(
                        modifier = Modifier.width(150.dp).height(50.dp),
                        shape = CircleShape,
                        onClick = onPunch,
                        enabled = clockMessage.isNullOrEmpty(),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = MaterialTheme.colorScheme.tertiary,
                            contentColor = MaterialTheme.colorScheme.onTertiary,
                            disabledContainerColor = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.5f),
                            disabledContentColor = MaterialTheme.colorScheme.onTertiary.copy(alpha = 0.5f),
                        ),
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            if (loadingPunch) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(24.dp),
                                    color = MaterialTheme.colorScheme.onTertiary
                                )
                            } else {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(
                                        modifier = Modifier.size(20.dp),
                                        painter = painterResource(Res.drawable.play_circle),
                                        contentDescription = null,
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(text = buttonText)
                                }
                            }
                        }
                    }
                }
            }
            if (!clockMessage.isNullOrEmpty()) {
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    textAlign = TextAlign.Center,
                    fontSize = 14.sp,
                    modifier = Modifier.fillMaxWidth(),
                    text = clockMessage
                )
            }
        }
    }
}